// apps/client/src/title/stage.ts (title screen, 2026-10-05) — the live 3D town behind the title, sign-in, the creator and the towns list:
// the showcase town (title/showcase.ts) built with the real world renderer and kits, lit for the player's local time of day (graded never
// to look murky, title/grade.ts), a few
// villagers strolling round the plaza, and the title camera (title/camera.ts) turning high above it until Play now swoops it down.
// It draws with the page's renderer and asset service (engine/pageRenderer.ts, assets/shared.ts), so the town the player enters next
// reuses its WebGL context, shader programs and kits; dispose() frees everything it made itself when that town takes over.
// Phones: the title draws at low quality unless the player chose high, the town is generated off the main thread, and the canvas
// fades in only once everything is built and its shaders are compiled (the title's sky gradient shows meanwhile).
import { tuning } from '@whiskerton/content';
import { rngFrom, type Vec2 } from '@whiskerton/shared';
import { buildNavGrid } from '@whiskerton/shared/map/nav';   // subpath-only (not in the barrel)
import * as THREE from 'three';
import type { AssetService } from '../assets/service';
import { IS_DEV, IS_E2E } from '../boot/env';
import { Lighting } from '../engine/lighting';
import type { Renderer } from '../engine/renderer';
import { setFadeCamera, setViewers, worldUniforms } from '../engine/shader/worldPatch';
import { bakedVillagerAvatar, type VillagerAvatar } from '../game/systems/villagers';
import { useGame } from '../state/store';
import { createOutdoorScene, type OutdoorScene } from '../world/outdoor';
import { fogAt, placeCamera, titleFrame, titleView } from './camera';
import { titleGrade } from './grade';
import { showcaseTown, type Showcase } from './showcase';
import type { ShowcaseReply, ShowcaseRequest } from './showcaseWorker';
import { Wanderer, wanderStarts } from './wander';

export type BackdropStatus = 'loading' | 'live' | 'disposed' | 'failed';
export interface StageDeps {
  renderer(): Renderer;
  assets(): AssetService;
  /** The showcase town; `cancel` stops it being made when the stage is disposed first. */
  showcase(): { town: Promise<Showcase>; cancel(): void };
  villager(assets: AssetService, v: Showcase['villagers'][number]): Promise<VillagerAvatar>;
  now(): number;
  raf(cb: (t: number) => void): number;
  caf(id: number): void;
  reducedMotion(): boolean;
  minuteOfDay(): number;
}
/** E2E builds draw on SwiftShader, where every browser context shares one software GPU: the backdrop keeps to a few frames a second so
 *  a second player's sign-in doesn't starve the first one's town (renderer.ts pins those builds to the lowest resolution too). */
const E2E_FPS = 4;
const FOV = tuning.camera.fovDeg;

/** The showcase town from a module Worker; without workers (tests, a WebView that refuses them) on the main thread, a macrotask later. */
export function loadShowcase(seed: number = tuning.title.seed): { town: Promise<Showcase>; cancel(): void } {
  let worker: Worker | null = null, cancelled = false;
  const here = () => new Promise<Showcase>((ok, fail) => setTimeout(() => { if (cancelled) fail(new Error('cancelled')); else ok(showcaseTown(seed)); }, 0));
  const town = typeof Worker === 'undefined' ? here() : new Promise<Showcase>((ok, fail) => {
    try { worker = new Worker(new URL('./showcaseWorker.ts', import.meta.url), { type: 'module' }); } catch { void here().then(ok, fail); return; }
    worker.onmessage = (e: MessageEvent<ShowcaseReply>) => {
      worker?.terminate();
      if (IS_DEV) console.info(`[title] showcase town built in ${Math.round(e.data.ms)} ms (worker)`);
      ok(e.data.showcase);
    };
    worker.onerror = (e) => { e.preventDefault(); worker?.terminate(); void here().then(ok, fail); };
    worker.postMessage({ seed } satisfies ShowcaseRequest);
  });
  return { town, cancel: () => { cancelled = true; worker?.terminate(); } };
}

type Stroller = { av: VillagerAvatar; w: Wanderer; walking: boolean | null };

export class TitleStage {
  status: BackdropStatus = 'loading';
  frames = 0;
  private disposed = false;
  private readonly deps: StageDeps;
  private renderer: Renderer | null = null;
  private outdoor: OutdoorScene | null = null;
  private lighting: Lighting | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private strollers: Stroller[] = [];
  private cancelShowcase: (() => void) | null = null;
  private raf = 0;
  private last = 0;
  private drawnAt = -Infinity;
  private readonly started: number;
  private readonly target = new THREE.Vector3();
  private townFog: [number, number] = [50, 150];
  private readonly onResize = () => {
    this.renderer?.resize();
    if (this.camera) { this.camera.aspect = innerWidth / innerHeight; this.camera.updateProjectionMatrix(); }
  };

  constructor(deps: StageDeps) { this.deps = deps; this.started = deps.now(); }

  /** Builds the town and starts the frame loop (its first frame marks it live); resolves then, or once the stage was disposed or failed
   *  meanwhile. */
  async start(): Promise<void> {
    mark('loading');
    try { await this.build(); }
    catch (e) {
      if (this.disposed) return;
      if (IS_DEV) console.warn('[title] the backdrop could not be built; the title shows over its sky', e);
      this.dispose();
      this.status = 'failed';
      return;
    }
    if (this.disposed) return;
    addEventListener('resize', this.onResize);
    const frame = (t: number) => { if (this.disposed) return; this.raf = this.deps.raf(frame); this.frame(t); };
    this.raf = this.deps.raf(frame);
  }

  /** The draw calls of the last frame's scene pass, for the test hooks (the budget is tuning.render.maxDrawCalls). */
  sceneCalls(): number { return this.renderer && this.frames > 0 && !this.disposed ? this.renderer.stats().sceneCalls : 0; }

  /** Frees everything the stage made (never the kits, nor the page's renderer, which the town takes over). */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.status = 'disposed';
    this.deps.caf(this.raf);
    removeEventListener('resize', this.onResize);
    this.cancelShowcase?.();
    for (const s of this.strollers) s.av.dispose();
    this.strollers = [];
    this.lighting?.dispose();
    this.outdoor?.dispose();
    this.lighting = null; this.outdoor = null; this.camera = null;
    const canvas = this.renderer?.renderer.domElement as HTMLCanvasElement | undefined;
    if (canvas) { canvas.style.opacity = ''; canvas.style.transition = ''; }
    mark(null);
  }

  private async build(): Promise<void> {
    const r = this.deps.renderer();
    this.renderer = r;
    const canvas = r.renderer.domElement as HTMLCanvasElement | undefined;
    if (canvas) { canvas.style.opacity = '0'; canvas.style.transition = 'opacity .6s var(--ease-out)'; }
    const load = this.deps.showcase();
    this.cancelShowcase = load.cancel;
    const show = await load.town;
    this.cancelShowcase = null;
    if (this.disposed) return;
    const assets = this.deps.assets();
    try { await assets.ready(); } catch { /* no kits (a dev checkout before pnpm assets): the terrain still draws */ }
    if (this.disposed) return;
    // Nothing fades over the title (no resident to follow, and the swoop must never lose a tree that crowds its camera): no ghosts, no
    // residents, a fade camera far out of the world.
    const outdoor = createOutdoorScene({ fade: false });
    setViewers([]);
    setFadeCamera(new THREE.Vector3(1e6, 1e6, 1e6));
    this.outdoor = outdoor;
    await outdoor.build(show.map, show.objects, { assets, town: show.town, residents: show.residents, you: null, now: Date.now(),
      heightAt: (p) => outdoor.heightAt(p) });
    if (this.disposed) return;
    this.lighting = new Lighting(outdoor.scene);                   // after build: build() clears the scene
    const fog = outdoor.scene.fog as THREE.Fog;
    this.townFog = [fog.near, fog.far];
    this.target.set(show.plaza.x, outdoor.heightAt(show.plaza), show.plaza.y);
    await this.addStrollers(show, assets);
    if (this.disposed) return;
    this.camera = new THREE.PerspectiveCamera(FOV, innerWidth / innerHeight, 0.3, 500);
    this.aim(this.deps.now());
    // Compile every shader before the first frame, so drawing it never stalls the page (Play now must answer at once).
    const compile = (r.renderer as { compileAsync?: (s: THREE.Object3D, c: THREE.Camera) => Promise<unknown> }).compileAsync;
    if (compile) await compile.call(r.renderer, outdoor.scene, this.camera).catch(() => {});
  }
  private async addStrollers(show: Showcase, assets: AssetService): Promise<void> {
    const n = Math.min(tuning.title.wanderers, show.villagers.length);
    if (n === 0) return;
    const nav = buildNavGrid(show.map, show.objects), rng = rngFrom('title', show.town.seed);
    const starts = wanderStarts(nav, show.plaza, n, rng.fork('starts'));
    const layer = this.outdoor!.layer('titleVillagers');
    const opts = { radius: tuning.title.wanderRadius, speed: tuning.villagers.walkSpeed, idle: tuning.title.wanderIdleSeconds };
    const made = await Promise.all(starts.map(async (start, i) => {
      try { return { av: await this.deps.villager(assets, show.villagers[i]!), start, i }; }
      catch { return null; }                                       // no rig (no kits): the town turns without them
    }));
    for (const m of made) {
      if (!m) continue;
      if (this.disposed) { m.av.dispose(); continue; }
      const w = new Wanderer(nav, show.map, show.plaza, m.start, rng.fork('walk', m.i), opts);
      layer.add(m.av.root);
      this.strollers.push({ av: m.av, w, walking: null });
    }
  }
  private aim(now: number): number {
    const s = useGame.getState();
    const view = titleView({ step: s.titleStep, motion: s.titleMotion, at: s.titleAt }, now, this.started, this.deps.reducedMotion());
    const cam = this.camera!, frame = titleFrame(view.pose, cam.aspect);   // framed for the screen's shape (upright: 2026-10-18)
    cam.fov = frame.fov;
    placeCamera(cam, this.target, view.pose, frame);
    const fog = this.outdoor!.scene.fog as THREE.Fog | null;
    if (fog) [fog.near, fog.far] = fogAt(view.pose, this.townFog);
    return view.veil;
  }
  private frame(t: number): void {
    const r = this.renderer, out = this.outdoor, lighting = this.lighting, cam = this.camera;
    if (!r || !out || !lighting || !cam) return;
    if (IS_E2E && t - this.drawnAt < 1000 / E2E_FPS) return;
    this.drawnAt = t;
    const dt = this.last ? Math.min(0.1, (t - this.last) / 1000) : 0;
    this.last = t;
    const veil = this.aim(this.deps.now());
    worldUniforms.uCamXZ.value.set(this.target.x, this.target.z);   // the curved world bends round the plaza
    worldUniforms.uTime.value += dt;
    out.tick(dt, lighting.update(this.deps.minuteOfDay(), this.target, titleGrade));   // never murky (title/grade.ts)
    for (const s of this.strollers) {
      s.w.update(dt);
      const p: Vec2 = s.w.pos;
      s.av.root.position.set(p.x, out.heightAt(p), p.y);
      s.av.root.rotation.y = s.w.facing;
      if (s.walking !== s.w.walking) { s.walking = s.w.walking; s.av.setMove(s.walking ? 'walk' : 'idle', s.walking ? tuning.villagers.walkSpeed : 0); }
      s.av.update(dt);
    }
    r.render(out.scene, cam);
    this.frames++;
    const canvas = r.renderer.domElement as HTMLCanvasElement | undefined;
    if (canvas) canvas.style.opacity = String(1 - veil);
    if (this.frames === 1) { this.status = 'live'; mark('live'); }
  }
}

/** `<html data-backdrop>`: 'loading' while the town builds (the pre-town screens drop their cream page and show the title sky), 'live'
 *  once it draws; absent with no backdrop. */
function mark(state: 'loading' | 'live' | null): void {
  if (typeof document === 'undefined') return;
  if (state) document.documentElement.dataset.backdrop = state;
  else delete document.documentElement.dataset.backdrop;
}

/** The stage's real dependencies: the page's renderer (at low quality unless the player chose high) and asset service. */
export function stageDeps(renderer: () => Renderer, assets: () => AssetService): StageDeps {
  return {
    renderer, assets, showcase: () => loadShowcase(),
    villager: (a, v) => bakedVillagerAvatar(a.rig(), v),
    now: () => performance.now(),
    raf: (cb) => requestAnimationFrame(cb), caf: (id) => cancelAnimationFrame(id),
    reducedMotion: () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
    minuteOfDay: () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); },
  };
}
